import React, { forwardRef, useContext } from 'react';
import { MenuItem as ChakraMenuItem, MenuItemProps } from '@chakra-ui/react';
import { Box, Flex } from '@allergan-data-labs/alle-elements-layout';
import { ActionMenuContext } from './action-menu-context';
import {
  getActionListHeaderStyles,
  getActionListItemDescriptionStyles,
  getActionListItemRightContentStyles,
  getActionListItemStyles,
  getActionListItemTextStyle,
  getActionListSubItemDescriptionStyles,
  getActionListChildrenContainerStyles,
  getActionListChildrenWrapperStyles,
} from './action-list-item.theme';

export const DESCRIPTION_CLASSNAME = 'action-list-item__description';
export const ICON_CLASSNAME = 'chakra-menu__icon-wrapper';
export const RIGHT_CONTENT_CLASSNAME = 'action-list-item__right-content';

export interface ActionListItemProps extends MenuItemProps {
  children: React.ReactNode;
  /**
   * Adds an additional description to the selection tile that is located
   * underneath the main selection tile label
   */
  description?: React.ReactNode;
  /**
   * Applies a active color state for the item shows the user that this item
   * is currently selected
   */
  active?: boolean;
  /**
   * Content to be displayed on the right side of the action list item.
   * This can be used for actions, indicators, or additional information.
   */
  rightContent?: React.ReactNode;
}

/**
 * The `ActionListItem` component is used for single click actions within the
 * `ActionMenu`. This component needs to be nested within the `ActionMenuGroup`
 * component.
 */
export const ActionListItem = forwardRef(
  (
    {
      children,
      description,
      rightContent,
      active = false,
      icon,
      ...props
    }: ActionListItemProps,
    ref
  ) => {
    const { colorMode } = useContext(ActionMenuContext);

    const renderRightContent = () => (
      <Box
        className={RIGHT_CONTENT_CLASSNAME}
        sx={getActionListItemRightContentStyles(colorMode)}
      >
        {rightContent}
      </Box>
    );

    const renderLeftIcon = () => <Box className={ICON_CLASSNAME}>{icon}</Box>;

    return (
      <ChakraMenuItem
        ref={ref}
        sx={getActionListItemStyles(colorMode)}
        {...props}
      >
        <Flex
          sx={{
            ...getActionListItemTextStyle(colorMode, active),
            ...(!icon
              ? getActionListHeaderStyles()
              : {
                  ...getActionListSubItemDescriptionStyles(),
                  ...getActionListChildrenContainerStyles(),
                }),
          }}
        >
          {icon && renderLeftIcon()}
          <Flex sx={getActionListChildrenWrapperStyles()}>
            {children}
            {description && (
              <Box
                className={DESCRIPTION_CLASSNAME}
                sx={getActionListItemDescriptionStyles(colorMode)}
              >
                {description}
              </Box>
            )}
          </Flex>
          {renderRightContent()}
        </Flex>
      </ChakraMenuItem>
    );
  }
);
